import type { PairSummaryDto } from '@rhcst/shared';
import { originLabel, originTone } from './OriginBadge.js';

/**
 * The provenance panel: where this pair came from and why we believe it.
 *
 * Its job is to make a classification checkable. Everything the scanner decided
 * is shown next to the evidence it decided from — contract, event, transaction,
 * block — so an operator who doubts a "Pons launch" label can follow it to a
 * transaction hash rather than take the badge's word for it.
 *
 * A label nobody can check is decoration, and decoration on a trading screen is
 * worse than nothing: it looks like a control.
 */

type Origin = NonNullable<PairSummaryDto['origin']>;

const BLOCK_REASON_TEXT: Record<string, string> = {
  SOURCE_UNVERIFIED: 'The source is not verified for trading.',
  POOL_NOT_LIVE: 'The token exists but has no live pool yet.',
  AUCTION_NOT_SETTLED: 'A Crowd Launch auction that has not settled.',
  KNOWN_TOKEN_NEW_LP: 'A new pool on a token we already knew, not a launch.',
  DEX_CANDIDATE_REQUIRES_MANUAL_REVIEW: 'Found only as a DEX pool. Needs manual review.',
  QUOTE_TOKEN_NOT_ALLOWED: 'Neither side is an allowed quote asset.',
  POOLKEY_UNEXPECTED: 'The v4 pool key does not match an expected launch shape.',
  PONS_PAIR_TOKEN_MISMATCH: 'A Pons launch paired against something other than WETH.',
  RISK_SCORE_TOO_LOW: 'The risk assessment did not clear the threshold.',
  KILL_SWITCH_ENABLED: 'The kill switch is engaged.',
};

function Row({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div className="origin-row">
      <span className="label label-dim">{label}</span>
      <span className={mono ? 'data-xs mono' : 'data-xs'}>
        {value ?? <span className="muted">not recorded</span>}
      </span>
    </div>
  );
}

export function OriginPanel({
  origin,
  pair,
}: {
  origin: Origin | null;
  pair: Pick<PairSummaryDto, 'factory_address' | 'created_tx_hash' | 'created_block' | 'address'>;
}) {
  if (origin === null) {
    return (
      <section className="panel">
        <h2 className="panel-title">Origin</h2>
        <p className="muted">
          This pair was discovered before the scanner recorded provenance, so where it came
          from was never captured. That is unknown rather than ordinary — it is not evidence
          that no launchpad was involved.
        </p>
        <Row label="Factory" value={pair.factory_address} mono />
        <Row label="Created in tx" value={pair.created_tx_hash} mono />
        <Row label="Block" value={pair.created_block} />
      </section>
    );
  }

  const tone = originTone(origin);

  return (
    <section className="panel">
      <h2 className="panel-title">Origin</h2>

      <p className="origin-headline" data-origin={tone}>
        {originLabel(origin)}
        {origin.platform && <span className="muted"> · {origin.platform}</span>}
      </p>

      {origin.classification_reason && (
        <p className="origin-reason">{origin.classification_reason}</p>
      )}

      <div className="origin-grid">
        <Row label="Event" value={origin.event_kind} />
        <Row label="Confidence" value={origin.confidence} />
        <Row label="Scanner source" value={origin.source_id} mono />
        <Row label="Pool version" value={origin.pool_version} />
        <Row label="Factory / contract" value={pair.factory_address} mono />
        <Row label="Pool" value={pair.address} mono />
        <Row label="Created in tx" value={pair.created_tx_hash} mono />
        <Row label="Block" value={pair.created_block} />
        <Row
          label="Canonical pool"
          value={origin.canonical_for_token ? 'yes — created by the launchpad' : 'no'}
        />
      </div>

      {origin.trade_block_reasons.length > 0 && (
        <div className="origin-blocked">
          <span className="label">Trading blocked</span>
          <ul>
            {origin.trade_block_reasons.map((reason) => (
              <li key={reason}>
                {BLOCK_REASON_TEXT[reason] ?? reason}
                <span className="muted mono"> ({reason})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
